import { memo, useCallback, useMemo, useState } from "react";

import type { Voucher, VoucherListInput } from "@/lib/gql/__generated__/graphql";

import { useDnD } from "@/hooks/use-dnd/use-dnd";

import { BlankContentForList } from "@/components/features/_components";
import {
	ListTableHeader,
	SortButton,
} from "@/components/features/items/edit/_shared/ui/components/system/list/list-table-header";
import { ItemListContainer } from "@/components/features/items/edit/_shared/ui/components/system/list/list-wrapper";

import { Table, TableBody, TableCaption, TableHeader } from "@/components/ui/table/table";

import { VoucherTableHeader } from "./header";
import { VoucherTableRow } from "./row";

type Props = {
	hotelID: string;
	sellingVouchers: Voucher[];
	stopSellingVouchers: Voucher[];
	onUpdateVoucherList: (input: VoucherListInput) => void;
	onDeleteVoucher: (hotelID: string, voucherID: string, voucherName: string) => void;

	navigateToCreateNewVoucherPage: () => void;
	navigateToDuplicateVoucherPage: (voucherID: string) => void;
	navigateToEditVoucherPage: (voucherID: string) => void;
};

export const VoucherListTableRoot = memo(
	({
		hotelID,
		sellingVouchers,
		stopSellingVouchers,
		onDeleteVoucher,
		onUpdateVoucherList: _onUpdateVoucherList,

		navigateToCreateNewVoucherPage,
		navigateToDuplicateVoucherPage,
		navigateToEditVoucherPage,
	}: Props) => {
		const { dndItemList, onCancelDnD: onCancelSort } = useDnD<Voucher>(sellingVouchers);

		const onSaveSort = useCallback(() => {
			_onUpdateVoucherList({
				hotelID,
				voucherList: dndItemList.map((voucher) => voucher.value.voucherID),
			});
		}, [_onUpdateVoucherList, dndItemList, hotelID]);

		const [isSorting, setIsSorting] = useState(false);
		const onChangeIsSorting = useCallback((isSorting: boolean) => setIsSorting(isSorting), []);

		/**
		 * 宿泊券の一覧を表示するか
		 */
		const canShowVoucherList = useMemo(() => dndItemList.length > 0, [dndItemList.length]);
		/**
		 * 非公開の宿泊券の一覧を表示するか
		 */
		const stopSellingVoucherList = useMemo(() => stopSellingVouchers.length > 0, [stopSellingVouchers.length]);

		return (
			<ItemListContainer
				header={
					<ListTableHeader
						leftContent={
							<SortButton
								isSorting={isSorting}
								onChangeIsSorting={onChangeIsSorting}
								onClickCancelSort={onCancelSort}
								onClickSaveSort={onSaveSort}
							/>
						}
						createNewItemList={[
							{
								title: "宿泊券を作成",
								handleClick: navigateToCreateNewVoucherPage,
							},
						]}
					/>
				}
				tableBody={
					<>
						<Table>
							<TableCaption>販売中の宿泊券の一覧</TableCaption>
							{canShowVoucherList ? (
								<>
									<VoucherTableHeader isSorting={isSorting} />
									<TableBody>
										{dndItemList.map((voucher, idx) => {
											const { voucherID } = voucher.value;

											return (
												<VoucherTableRow
													key={voucher.value.voucherID}
													voucher={voucher.value}
													idx={idx}
													dndEvents={voucher.events}
													handleClickTableRow={() => navigateToEditVoucherPage(voucherID)}
													iconButtonActionsList={{
														onClickDeleteButton: () =>
															onDeleteVoucher(hotelID, voucherID, voucher.value.name),
														onClickCopyButton: () =>
															navigateToDuplicateVoucherPage(voucherID),
														onClickEditButton: () => navigateToEditVoucherPage(voucherID),
													}}
													isSorting={isSorting}
												/>
											);
										})}
									</TableBody>
								</>
							) : (
								<BlankContentForList
									content={`販売中の宿泊券はありません。\n新規作成ボタンから宿泊券を作成してください。`}
								/>
							)}
						</Table>

						<Table>
							<TableCaption>非公開の宿泊券の一覧</TableCaption>
							<TableHeader />
							<TableBody>
								{stopSellingVoucherList ? (
									<>
										{stopSellingVouchers.map((voucher, idx) => {
											const { voucherID } = voucher;

											return (
												<VoucherTableRow
													key={voucherID}
													voucher={voucher}
													idx={idx}
													dndEvents={undefined}
													handleClickTableRow={() => navigateToEditVoucherPage(voucherID)}
													iconButtonActionsList={{
														onClickDeleteButton: () =>
															onDeleteVoucher(hotelID, voucherID, voucher.name),
														onClickCopyButton: () =>
															navigateToDuplicateVoucherPage(voucherID),
														onClickEditButton: () => navigateToEditVoucherPage(voucherID),
													}}
													isSorting={isSorting}
												/>
											);
										})}
									</>
								) : (
									<BlankContentForList content="非公開の宿泊券はありません。" />
								)}
							</TableBody>
						</Table>
					</>
				}
			/>
		);
	},
);

VoucherListTableRoot.displayName = "VoucherListTableRoot";
